import React, { FunctionComponent, useContext, useState } from 'react';
import Markdown from 'markdown-to-jsx';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Alert, Button, Card, Container, Snackbar } from '@mui/material';
import Editor from "@monaco-editor/react";
import { AppContext } from '../../providers/AppContext';
import { SectionType } from '../../enums/SectionType';
import { ThemeContext } from '../../providers/ThemeContext';
import CustomizedDialogs from '../../components/Dialog';
import Visibility from '@mui/icons-material/Visibility';
import Code from '@mui/icons-material/Code';

interface PreviewProps {
    children?: React.ReactNode;
    index?: number;
    value?: number;
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

function TabPanel(props: PreviewProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role='tabpanel'
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

const Preview: FunctionComponent<PreviewProps> = (props: PreviewProps) => {

    const { about, description, fullName, username, sections, socialMedia, tech } = useContext(AppContext);
    const { darkMode } = React.useContext(ThemeContext);

    const [input, setInput] = useState(`
<h1 align="center">Hi 👋, I'm ${fullName}</h1>
<h3 align="center">I'm passionate on ${description}</h3>

${sections.map(item => {
        if (item.name === SectionType.ABOUT) {
            return `
##  🥷🏻 About Me

${about.map(item => `- ${item.description} [${item.value}](${item.link})`).join('\n\n')}
`
        } else if (item.name === SectionType.LNT) {
            return `
## 🚀 Languages and Tools:
<p align="left">
${tech.filter(item => item.choosed).map(item => item.imageUrl).join(' ')}
</p>`
        } else if (item.name === SectionType.STATS) {
            return `
## 💻 My Github Stats

<p><img align="center" src="https://github-readme-stats.vercel.app/api/top-langs?username=${username}&show_icons=true&locale=en&layout=compact&theme=${darkMode ? 'react' : 'default'}&hide_border=true&bg_color=${darkMode ? '060A0CD0' : 'DBDBDB'}" alt="${username}" style="max-width: 100%;"/></p>

<p><img align="center" src="https://github-readme-stats.vercel.app/api?username=${username}&show_icons=true&locale=en&theme=${darkMode ? 'react' : 'default'}&hide_border=true&bg_color=${darkMode ? '060A0CD0' : 'DBDBDB'}" alt="${username}" style="max-width: 100%;"/></p>`
        } else if (item.name === SectionType.CONNECT) {
            return `
## Connect with me :            
<p align="left">
${socialMedia.filter(item => item.value !== '').map(item => `<a href="${item.value}" target="blank">${item.imageUrl}</a>`).join('')}
</p>
            `
        } else if (item.name === SectionType.STREAK) {
            return `
## 🔥 Github Streak
<p align="center" ><img align="center" src="https://github-readme-streak-stats.herokuapp.com/?user=${username}&&theme=${darkMode ? 'react' : 'default'}&hide_border=true&stroke=0000&background=${darkMode ? '060A0CD0' : 'DBDBDB'}" alt="${username}"  style="max-width: 100%;"/></p>`
        } else if (item.name === SectionType.TROPHY) {
            return `<p align="center"> <a href="https://github.com/ryo-ma/github-profile-trophy"><img src="https://github-profile-trophy.vercel.app/?username=${username}&row=1&column=6&margin-w=15" alt="${username}" /></a> </p>`
        } else {
            return ''
        }
    }).join('')}
    `);

    const [open, setOpen] = useState(false);
    const handleEditorChange = (value: any) => {
        setInput(value);
    }
    const [openDialog, setOpenDialog] = React.useState(false);

    const handleClickOpenDialog = () => {
        setOpenDialog(true);
    };
    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const [value, setValue] = useState(0);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const onDownload = () => {
        const blob = new Blob([input], { type: 'application/json' })
        const fileDownloadUrl = URL.createObjectURL(blob);
        const tempLink = document.createElement('a');
        tempLink.href = fileDownloadUrl;
        tempLink.setAttribute('download', `README.md`);
        tempLink.click();
        URL.revokeObjectURL(fileDownloadUrl);

        handleClickOpenDialog()
    }

    const onCopy = () => {
        navigator.clipboard.writeText(input)
        setOpen(true)
        handleClickOpenDialog()
    }

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpen(false);
    };

    return (
        <div style={{ backgroundColor: darkMode ? 'black' : 'white' }}>
            <Container style={{
                display: 'flex',
                flexDirection: 'row',
                padding: 24
            }}>
                <Box sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs indicatorColor={darkMode ? 'primary' : 'secondary'} value={value} onChange={handleChange} aria-label='basic tabs example'>
                            <Tab style={{ color: darkMode ? 'whitesmoke' : '#212121' }} label={
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    <Visibility style={{ marginRight: 12 }} />
                                    Preview
                                </div>
                            } {...a11yProps(0)} />
                            <Tab style={{ color: darkMode ? 'whitesmoke' : '#212121' }} label={
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    <Code style={{ marginRight: 12 }} />
                                    Edit
                                </div>
                            } {...a11yProps(1)} />
                        </Tabs>
                    </Box>
                    <TabPanel value={value} index={0}>
                        <Card style={{ paddingInline: 24 }}>
                            <Markdown>
                                {
                                    input
                                }
                            </Markdown>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'end',
                                marginBlock: 60,
                            }}>
                                <Button variant='contained' onClick={onDownload}>
                                    Download as README.md
                                </Button>
                                <Button variant='contained' style={{
                                    marginLeft: 15
                                }}
                                    onClick={onCopy}
                                >
                                    Copy text
                                </Button>
                            </div>

                        </Card>

                    </TabPanel>
                    <TabPanel value={value} index={1}>
                        <Card style={{ paddingBlock: 24 }}>
                            <Editor
                                height="85vh"
                                language={"markdown"}
                                value={input}
                                theme={darkMode ? "vs-dark" : 'vs-light'}
                                defaultValue="// some comment"
                                onChange={handleEditorChange}
                            />
                        </Card>
                    </TabPanel>
                </Box>
            </Container>
            <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
                <Alert onClose={handleClose} severity="info" sx={{ width: '100%' }}>
                    Copied to clipboard
                </Alert>
            </Snackbar>

            <CustomizedDialogs open={openDialog} handleClose={handleCloseDialog} />

        </div>
    );
}

export default Preview;